import React, { useEffect, useState } from 'react';
import { Alert, Button, Space, Table, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchStockPurchases, StockPurchase } from './stockValuationApi';

export default function StockPurchasesReport() {
  const { t } = useTranslation('orders');
  const [rows, setRows] = useState<StockPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const result = await fetchStockPurchases();
      setRows(result.rows);
      setTruncated(result.truncated);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const key = (name: string) => t(`supplierOrders.stockValuation.${name}`);
  const money = (value: number | null) =>
    value == null
      ? '—'
      : new Intl.NumberFormat('pl-PL', {
          style: 'currency',
          currency: 'PLN',
        }).format(value);
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Typography.Paragraph>{key('reportHelp')}</Typography.Paragraph>
      <Button onClick={load}>{key('refresh')}</Button>
      {failed && <Alert type="error" message={key('loadFailed')} />}
      {truncated && <Alert type="warning" message={key('truncated')} />}
      <Table<StockPurchase>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 25 }}
        columns={[
          {
            title: key('order'),
            render: (_, r) => (
              <Link to={`/app/supplier-orders/${r.id}`}>
                {r.useManualNumbering ? r.manualOrderNumber : r.orderNumber}
              </Link>
            ),
          },
          { title: key('supplier'), dataIndex: 'supplierName' },
          ...(
            [
              'goodsPLN',
              'landedPLN',
              'freightPLN',
              'unreceivedExtraPLN',
              'remainingValuePLN',
            ] as const
          ).map((field) => ({
            title: key(field),
            dataIndex: field,
            render: money,
          })),
          {
            title: key('valuation'),
            render: (_, r) => (
              <Tag color={r.valuationComplete ? 'green' : 'orange'}>
                {key(r.valuationComplete ? 'complete' : 'incomplete')}
              </Tag>
            ),
          },
        ]}
      />
    </Space>
  );
}
